import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ProductCard from './ProductCard';
import {
  useMinisiteProducts, isMinisite, occasionOf, MinisiteProductCard, GalerieInvitesTeaser,
} from './MinisiteCatalogPage';
import { useMinisiteOccasions } from '../utils/minisiteOccasions';
import { useLifeEventPage } from '../utils/lifeEvents';
import { useProductTexts } from '../utils/productI18n';
import { useT } from '../i18n';
import { API_BASE } from '../utils/apiBase';
import { markdownToExcerpt } from '../utils/text';
import Faq from './Faq';
import Seo from './Seo';

const MODELES_PAR_OCCASION = 6;

// Page d'un événement de vie (/faire-part-mariage, /faire-part-naissance…) : tout ce que la boutique
// propose pour cet événement — faire-part et invitations animés, cartes à gratter, galerie des
// invités — avec les textes de référencement (locales/<langue>/life-event-pages.json, via lifeEvents.js).
const LifeEventPage = ({ eventKey }) => {
  const t = useT('life-event-pages');
  const event = useLifeEventPage(eventKey); // titre, méta, introduction, articles dans la langue courante
  const OCCASIONS = useMinisiteOccasions(); // vocabulaire des gammes (lot « catalogue des faire-part »), traduit
  const tp = useProductTexts(); // titres / descriptions / étiquettes des produits en base, traduits (repli : français)
  const typed = useMinisiteProducts();
  const [cartes, setCartes] = useState([]);

  useEffect(() => {
    axios.get(`${API_BASE}/api-2026.php`, {
      params: { cleApi: '34KVHFQSP226YTk4n256QP6rGbqhA5w2ymY7W3pLwdbuc3i75', service: 'getProducts' },
    })
      .then((response) => setCartes((response.data?.data || []).filter((p) => !p.option_schema
        && p.visuels?.length > 0 && event.cartes.test(`${p.url} ${p.titre}`))))
      .catch(() => setCartes([]));
  }, [event.cartes]); // filtre des cartes de l'événement (constante, ne dépend pas de la langue)

  return (
    <Box sx={{ padding: { xs: 1, sm: 3 } }}>
      <Seo titre={event.metaTitle} description={event.metaDescription} path={event.path} image={event.image} />

      <Typography variant="h1" sx={{ textAlign: 'center', marginBottom: 2 }}>{event.titre}</Typography>
      <Typography sx={{ maxWidth: 780, margin: '0 auto 40px', textAlign: 'center' }}>{event.intro}</Typography>

      {event.occasions.map((key) => {
        const occasion = OCCASIONS[key];
        const modeles = (typed || []).filter((p) => isMinisite(p) && occasionOf(p) === key);
        if (typed !== null && modeles.length === 0) return null;
        return (
          <Box key={key} sx={{ marginBottom: 7 }}>
            <Typography variant="h2" component="h2" sx={{ textAlign: 'center', marginBottom: 1 }}>{occasion.hub.titre}</Typography>
            <Typography sx={{ maxWidth: 760, margin: '0 auto 24px', textAlign: 'center', color: '#7a6060' }}>{occasion.hub.texte}</Typography>
            <Grid container spacing={3}>
              {modeles.slice(0, MODELES_PAR_OCCASION).map((product) => (
                <Grid item xs={12} sm={6} md={4} key={product.id}><MinisiteProductCard product={product} /></Grid>
              ))}
            </Grid>
            <Box sx={{ textAlign: 'center', marginTop: 3 }}>
              <Button variant="outlined" color="secondary" component={Link} to={occasion.hub.path}>
                {t(`life-event-pages.occasions.${key}.viewAll`, { n: modeles.length })}
              </Button>
            </Box>
          </Box>
        );
      })}

      {cartes.length > 0 && (
        <Box sx={{ marginBottom: 7 }}>
          <Typography variant="h2" component="h2" sx={{ textAlign: 'center', marginBottom: 1 }}>{event.cartesTitre}</Typography>
          <Typography sx={{ maxWidth: 760, margin: '0 auto 24px', textAlign: 'center', color: '#7a6060' }}>{event.cartesTexte}</Typography>
          <Grid container spacing={3}>
            {cartes.map((product) => {
              const texts = tp(product); // même produit, textes traduits s'ils existent
              return (
                <Grid item xs={12} sm={6} md={4} key={product.id}>
                  <ProductCard
                    visuels={product.visuels}
                    title={texts.titre}
                    price={parseFloat(product.prix_ttc) || 0}
                    badge={texts.etiquette}
                    linkTo={`/produit/${product.url}`}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                      {markdownToExcerpt(texts.description_courte || texts.description, 100)}
                    </Typography>
                    <Button variant="contained" color="secondary" sx={{ marginTop: 2 }} component={Link} to={`/produit/${product.url}`}>
                      {t('life-event-pages.buttons.viewDetails')}
                    </Button>
                  </ProductCard>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {event.galerie && <GalerieInvitesTeaser products={typed} />}

      <Box sx={{ maxWidth: 820, margin: '56px auto 0' }}>
        {event.seo.map((bloc) => (
          <Box key={bloc.id} sx={{ marginBottom: 4 }}>
            <Typography variant="h4" component="h2" sx={{ marginBottom: 1 }}>{bloc.titre}</Typography>
            <Typography>{bloc.texte}</Typography>
          </Box>
        ))}
      </Box>
      <Faq page={`evt-${event.key}`} />
    </Box>
  );
};

export default LifeEventPage;
